import React, { createContext, useContext, useState, useCallback } from 'react';
import { Loader } from '../components/Loader/Loader';

const LoadingContext = createContext();

/**
 * LoadingProvider - Global loading state management
 *
 * Usage:
 * ```jsx
 * import { useLoading } from '../contexts/LoadingContext';
 *
 * function MyComponent() {
 *   const { showLoading, hideLoading, setLoadingMessage } = useLoading();
 *
 *   const fetchData = async () => {
 *     showLoading('Loading data...');
 *     try {
 *       await api.fetch();
 *     } finally {
 *       hideLoading();
 *     }
 *   };
 * }
 * ```
 */
export const LoadingProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const showLoading = useCallback((loadingMessage = '') => {
    setMessage(loadingMessage);
    setLoading(true);
  }, []);

  const hideLoading = useCallback(() => {
    setLoading(false);
    setMessage('');
  }, []);

  const setLoadingMessage = useCallback(loadingMessage => {
    setMessage(loadingMessage);
  }, []);

  const value = {
    loading,
    message,
    showLoading,
    hideLoading,
    setLoadingMessage,
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
      <Loader loading={loading} message={message} size="fullscreen" fullscreen />
    </LoadingContext.Provider>
  );
};

/**
 * Hook to access loading context
 * @returns {Object} Loading context with { loading, message, showLoading, hideLoading, setLoadingMessage }
 */
export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

/**
 * Higher-order component to inject loading state into components
 * @param {React.Component} Component - Component to wrap
 * @returns {React.Component} Wrapped component with loading prop
 */
export const withLoading = Component => {
  return function WithLoadingComponent(props) {
    const loading = useLoading();
    return <Component {...props} loading={loading} />;
  };
};

export default LoadingContext;
